import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Paper,
  Button,
  Divider,
  Link
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

interface JsonFileViewerProps {
  open: boolean;
  onClose: () => void;
  fileOrData: File | Blob | string | object | null;
  title?: string;
}

interface FileLinkProps {
  fileOrData: File | Blob | string | object | null;
  linkText?: string;
}

const JsonFileViewer: React.FC<JsonFileViewerProps> = ({ 
  open, 
  onClose, 
  fileOrData, 
  title 
}) => {
  const [jsonContent, setJsonContent] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!fileOrData || !open) return;
    
    // Reset states when modal opens
    setJsonContent(null);
    setError(null);
    
    try {
      // Handle different types of input
      if (fileOrData instanceof Blob) {
        // If it's a File/Blob object
        const reader = new FileReader();
        
        reader.onload = (event) => {
          try {
            const content = JSON.parse(event.target?.result as string);
            setJsonContent(content);
          } catch (err) {
            setError("Invalid JSON format");
          }
        };
        
        reader.onerror = () => {
          setError("Error reading file");
        };
        
        reader.readAsText(fileOrData);
      } else if (typeof fileOrData === 'string') {
        // If it's a JSON string
        try {
          const content = JSON.parse(fileOrData);
          setJsonContent(content);
        } catch (err) {
          setError("Invalid JSON string");
        }
      } else if (typeof fileOrData === 'object' && fileOrData !== null) {
        // If it's already a JS object
        setJsonContent(fileOrData);
      } else {
        setError("Unsupported data format");
      }
    } catch (err) {
      setError("Error processing data");
    }
  }, [fileOrData, open]);

  const handleCopyToClipboard = () => {
    if (jsonContent) {
      navigator.clipboard.writeText(JSON.stringify(jsonContent, null, 2));
    }
  };

  const handleDownload = () => {
    if (jsonContent) {
      const blob = new Blob([JSON.stringify(jsonContent, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = (fileOrData instanceof File ? fileOrData.name : 'data') || 'file.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="json-file-modal-title"
    >
      <Paper
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '80%',
          maxWidth: 800,
          maxHeight: '90vh',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 0,
          borderRadius: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header */}
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography id="json-file-modal-title" variant="h6" component="h2">
            {title || (fileOrData instanceof File ? fileOrData.name : 'JSON Viewer')}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        
        <Divider />
        
        {false && <Box sx={{ p: 1, display: 'flex', gap: 1, bgcolor: '#f5f5f5' }}>
          <Button 
            size="small" 
            startIcon={<ContentCopyIcon />} 
            onClick={handleCopyToClipboard}
            disabled={!jsonContent}
          >
            Copy
          </Button>
          <Button 
            size="small" 
            startIcon={<DownloadIcon />} 
            onClick={handleDownload}
            disabled={!jsonContent}
          >
            Download
          </Button>
        </Box>}
        
        <Divider />
        
        <Box 
          sx={{ 
            p: 3, 
            overflow: 'auto',
            flexGrow: 1,
            fontFamily: 'monospace',
            bgcolor: '#f8f8f8',
            mb: 6,
          }}
        >
          {error ? (
            <Typography color="error">{error}</Typography>
          ) : !jsonContent ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <Typography>Loading file content...</Typography>
            </Box>
          ) : (
            <pre style={{ margin: 0, minHeight: '300px', whiteSpace: 'normal', wordWrap: 'break-word' }}>
              {JSON.stringify(jsonContent, null, 2)}
            </pre>
          )}
        </Box>
      </Paper>
    </Modal>
  );
};

const FileLink: React.FC<FileLinkProps> = ({ fileOrData, linkText }) => {
  const [modalOpen, setModalOpen] = useState(false);

  const handleOpen = () => {
    if (fileOrData) {
      setModalOpen(true);
    }
  };

  const handleClose = () => {
    setModalOpen(false);
  };

  return (
    <>
      <Link 
        component="button"
        className="fs-13" 
        onClick={handleOpen}
        sx={{ textDecoration: 'none', cursor: 'pointer' }}
      >
        {linkText || 'View'}
      </Link>
      
      <JsonFileViewer
        open={modalOpen}
        onClose={handleClose}
        fileOrData={fileOrData}
        title={fileOrData instanceof File ? fileOrData.name : 'JSON Data'}
      />
    </>
  );
};

export { JsonFileViewer, FileLink };